import { AttributeCategoryCacheItem, AttributeCategoryInfo, AttributeSelectorModel, AttributeType, AttributeValueCacheInformation, AttributeValueModel } from "src/shared/models/attribute-selector.models";
import { ClearViewUtils } from "../utils";
import { CvApiServiceBase } from "./cv-api-service-base";
import { CvLocalizationServiceBase } from "./cv-localization-service-base";

export class AttributeSelectorServiceBase {

    public localizationText = {
        all: "All",
        notSet: "Not Set",
        noAttributes: "Not Set",
        attributes: "Attributes",
        categories: "Categories",
        cxoneNotSet: "N/A",
    };

    public attributeValues: {[type: number]: {[categoryId: number]: AttributeValueModel[]}} = {
        0: {},
        1: {},
    };

    private _attributeValueCacheInfo: {[type: number]: {[categoryId: number]: AttributeValueCacheInformation}} = {
        0: {},
        1: {},
    };

    private dataCache = new AttributeCategoryCacheItem();
    private userCache = new AttributeCategoryCacheItem();

    constructor(
        fromSharedService: boolean = false,
        private cvApiService?: CvApiServiceBase,
        cvLocalizationService?: CvLocalizationServiceBase,
    ) {
        if (fromSharedService)
            return AttributeSelectorServiceBase.getServiceBase();

        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();
        if (!cvLocalizationService)
            cvLocalizationService = CvLocalizationServiceBase.getServiceBase();

        cvLocalizationService.GetLocalText("Common", "All", "All").then(val => this.localizationText.all = val);
        cvLocalizationService.GetLocalText("Controls", "Not_Set", "Not Set").then(val => this.localizationText.notSet = val);
        cvLocalizationService.GetLocalText("Controls", "Attributes", "Attributes").then(val => this.localizationText.attributes = val);
        cvLocalizationService.GetLocalText("Controls", "Categories", "Categories").then(val => this.localizationText.categories = val);
        cvLocalizationService.GetLocalText("Controls", "Attributes_None", "No Attributes Chosen").then(
            val => this.localizationText.noAttributes = val
        );
        cvLocalizationService.GetLocalText("Controls", "CXOne_Not_Set", "N/A").then(val => this.localizationText.cxoneNotSet = val);
    }

    static getServiceBase(): AttributeSelectorServiceBase {
        return ClearViewUtils.getSharedService(AttributeSelectorServiceBase, 'AttributeSelectorServiceBase');
    }

    async loadSelectorModel(pageName: string, type: AttributeType, creatorId?: number) {
        let uri = `/api/v1/attribute-selector/${pageName}?type=${type}`;
        if (creatorId)
            uri += `&creatorId=${creatorId}`;
        const response = await this.cvApiService.get<AttributeSelectorModel>(uri);
        return response.data;
    }

    async saveSelectorModel(pageName: string, model: AttributeSelectorModel, creatorId?: number) {
        let uri = `/api/v1/attribute-selector/${pageName}`;
        if (creatorId)
            uri += `?creatorId=${creatorId}`;
        await this.cvApiService.post(uri, model);
    }

    async loadCategories(type: AttributeType) {
        const cache = type === AttributeType.User ? this.userCache : this.dataCache;

        if (cache.CachedCategories != null && Date.now() < cache.CategoryCacheTime + (5 * 60 * 1000))
            return cache.CachedCategories;

        if (cache.CategoryCachePromise == null)
            cache.CategoryCachePromise = this.cvApiService.get<AttributeCategoryInfo>(`/api/v1/attribute-selector/categories?type=${type}`);

        const response = await cache.CategoryCachePromise;
        cache.CategoryCacheTime = Date.now();
        return response.data;
    }

    async loadValues(categoryId: number, type: AttributeType) {
        const cacheInfo = this._attributeValueCacheInfo[type][categoryId] ?? new AttributeValueCacheInformation();

        if (this.attributeValues[type][categoryId] != null && Date.now() < cacheInfo.CacheTime + (5 * 60 * 1000))
            return this.attributeValues[type][categoryId];

        if (cacheInfo.CachePromise == null) {
            const uri = `/api/v1/attribute-selector/category/${categoryId}/values?type=${type}`;
            cacheInfo.CachePromise = this.cvApiService.get<AttributeValueModel[]>(uri);
        }

        const response = await cacheInfo.CachePromise;
        cacheInfo.CacheTime = Date.now();
        this.attributeValues[type][categoryId] = response.data;
        return response.data;
    }
}
